"use client";

import { AnimatePresence, m } from "framer-motion";
import NextImage from "next/image";
import Link from "next/link";
import { useEffect, useState } from "react";
import { useTrialForm } from "@/components/providers/TrialFormProvider";
import { cdnUrl } from "@/lib/cdn";
import TrustBar from "./TrustBar";

/* Product screenshot — the dark "AI Search & Screening" dashboard in a monitor
   mockup on a transparent background. It straddles the white field above and
   the full-bleed dark shelf below, which in turn meets the dark Features
   section (bg #0a0a0a) with no visible seam. */
const productImage = cdnUrl(
  "/images/uploads/1788369632137-ai-search-screening-monitor-no-led.png",
);

const ROTATING_WORDS = [
  "Catalysts",
  "Guidance",
  "Operating Metrics",
  "Fraud Detection",
  "AI Search",
];

export default function Hero() {
  const { openTrialForm } = useTrialForm();
  const [wordIndex, setWordIndex] = useState(0);

  useEffect(() => {
    const id = setInterval(() => {
      setWordIndex((i) => (i + 1) % ROTATING_WORDS.length);
    }, 2200);
    return () => clearInterval(id);
  }, []);

  const word = ROTATING_WORDS[wordIndex];

  return (
    <section className="relative bg-white overflow-hidden">
      {/* ── Intro — two columns on a clean white field ── */}
      <div className="relative z-10 max-w-[1400px] mx-auto px-5 sm:px-6 lg:px-8 pt-32 sm:pt-40 lg:pt-44 pb-10 sm:pb-14">
        <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          {/* Left — headline + trust pill */}
          <div>
            <h1
              className="text-[clamp(2rem,4.23vw,3.84rem)] font-[family-name:var(--font-display)] leading-[1] tracking-[-0.035em] text-neutral-900"
            >
              {/* Real text for crawlers and screen readers: aria-label on an <h1>
                  is ignored by search engines, and the animated spans below only
                  ever server-render one rotating word. */}
              <span className="sr-only">
                The AI Co-Analyst for Catalysts, Guidance, Operating Metrics, Fraud
                Detection, and AI Search
              </span>
              <span aria-hidden="true">The Co-Analyst for</span>
              {/* Rotating word — own fixed-height, clipped block so it can never
                  spill into the trust pill below or the subtitle beside it. */}
              <span
                aria-hidden="true"
                className="relative block h-[1.1em] overflow-hidden"
              >
                <AnimatePresence mode="wait" initial={false}>
                  <m.span
                    key={word}
                    initial={{ y: "110%" }}
                    animate={{ y: "0%" }}
                    exit={{ y: "-110%" }}
                    transition={{ duration: 0.5, ease: [0.76, 0, 0.24, 1] }}
                    className="absolute inset-x-0 top-0 text-violet-800"
                  >
                    {word}
                  </m.span>
                </AnimatePresence>
              </span>
            </h1>

            {/* Trust pill */}
            <div className="mt-8 sm:mt-10">
              <div className="inline-flex items-center gap-2 sm:gap-6 px-3 sm:px-6 py-1.5 sm:py-3 rounded-lg sm:rounded-xl border border-black/[0.08] bg-black/[0.015]">
                <span className="text-[9px] sm:text-[10px] font-medium tracking-[0.2em] sm:tracking-[0.25em] uppercase text-neutral-400">
                  Trusted by
                </span>
                <div className="w-px h-3 sm:h-4 bg-black/10" />
                <div className="flex items-center gap-1.5 sm:gap-2">
                  <span className="text-xs sm:text-base font-semibold text-neutral-900 tabular-nums">$600B+</span>
                  <span className="text-[10px] sm:text-xs text-neutral-400">AUM</span>
                </div>
                <div className="w-px h-3 sm:h-4 bg-black/10" />
                <div className="flex items-center gap-1.5 sm:gap-2">
                  <span className="text-[10px] sm:text-xs text-neutral-400">Fortune</span>
                  <span className="text-xs sm:text-base font-semibold text-neutral-900">100</span>
                </div>
                <div className="w-px h-3 sm:h-4 bg-black/10" />
                <div className="flex items-center gap-1.5 sm:gap-2">
                  <span className="text-[10px] sm:text-xs text-neutral-400">Top</span>
                  <span className="text-xs sm:text-base font-semibold text-neutral-900">5</span>
                  <span className="text-[10px] sm:text-xs text-neutral-400">Law Firms</span>
                </div>
              </div>
            </div>
          </div>

          {/* Right — subtitle + CTA */}
          <div className="lg:pl-8">
            <p className="text-lg sm:text-xl lg:text-2xl text-neutral-800 font-light tracking-tight leading-snug">
              Institutional AI for US public markets research
            </p>

            <div className="mt-6 sm:mt-8 flex flex-col items-start gap-3">
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 sm:gap-6">
                <m.button
                  onClick={openTrialForm}
                  whileHover={{ scale: 1.04 }}
                  whileTap={{ scale: 0.97 }}
                  className="group relative inline-flex items-center gap-3 px-8 py-4 bg-black text-white rounded-md overflow-hidden"
                >
                  <span className="absolute inset-0 bg-violet-800 translate-y-full group-hover:translate-y-0 transition-transform duration-500 ease-[cubic-bezier(0.76,0,0.24,1)]" />
                  <span className="relative text-sm font-medium">Start Free Trial</span>
                  <span className="relative w-5 h-5 rounded-full bg-white/20 flex items-center justify-center group-hover:bg-white/30 transition-colors">
                    <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M17 8l4 4m0 0l-4 4m4-4H3" />
                    </svg>
                  </span>
                </m.button>
                <Link href="/products" className="text-sm text-neutral-600 hover:text-black transition-colors duration-500 underline underline-offset-4 decoration-black/30 hover:decoration-black/60">
                  See the Co-Analyst&apos;s top features
                </Link>
              </div>
              <span className="text-sm text-neutral-500">
                14-day free trial · from $119 monthly
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* ── Product — dark monitor straddling a full-bleed dark shelf ── */}
      <div className="relative">
        {/* The shelf: full-bleed #0a0a0a with a crisp top edge that lands ~a
            quarter of the way down the screenshot, then runs to the bottom
            where it meets the dark Features section. */}
        <div className="absolute inset-x-0 top-[24%] bottom-0 bg-[#0a0a0a]" aria-hidden="true" />

        {/* Monitor mockup — transparent PNG, so no card chrome (border/ring/
            shadow) that would draw a box across the empty transparent margins. */}
        <div className="relative max-w-[1400px] mx-auto px-5 sm:px-6 lg:px-8">
          <NextImage
            src={productImage}
            alt="Hudson Labs Co-Analyst — AI Search &amp; Screening"
            width={3960}
            height={2906}
            priority
            sizes="(max-width: 1400px) 124vw, 1653px"
            className="w-[123.75%] max-w-none ml-[-11.875%] h-auto"
          />
        </div>
      </div>

      {/* Press logos — sit on the dark shelf, at the bottom of the hero. */}
      <div className="relative z-10 bg-[#0a0a0a]">
        <TrustBar />
      </div>
    </section>
  );
}
